import { useId, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Field, ToggleRow, StepAlert } from "../ui";
import { calibrationCheck, calibrationError, numberOrNull } from "../calibration";
import TankSizeHelper from "./TankSizeHelper";
import DeviceTestPanel from "./DeviceTestPanel";

function TankDiagram({ mount, t }) {
  const titleId = useId();
  return (
    <Box component="figure" m={0} my={2} sx={{ color: t.textStrong }}>
      <svg viewBox="0 0 400 245" role="img" aria-labelledby={titleId}
        style={{ display: "block", width: "100%", height: "auto" }}>
        <title id={titleId}>
          Measure sensor height and full point up from the tank floor.
          Empty is the floor. Full is usually the bottom of the inlet.
        </title>
        <rect x="55" y="45" width="170" height="160" rx="8"
          fill={t.softSurface} stroke="currentColor" strokeWidth="2" />
        <rect x="57" y="130" width="166" height="73" fill={t.primarySoft} />
        <path d="M10 72h47v18H10" fill={t.softSurface}
          stroke="currentColor" strokeWidth="2" />
        <text x="10" y="65" fontSize="12" fill="currentColor">Inlet</text>
        <path d="M50 90h185" stroke="currentColor" strokeDasharray="6 4" />
        <text x="240" y="87" fontSize="12" fill="currentColor">Full point (100%)</text>
        <text x="240" y="103" fontSize="11" fill="currentColor">Bottom of inlet</text>
        <path d="M50 205h185" stroke="currentColor" strokeDasharray="6 4" />
        <text x="240" y="201" fontSize="12" fill="currentColor">Empty point (0%)</text>
        <text x="240" y="217" fontSize="11" fill="currentColor">Tank floor = 0 in</text>
        {mount === "top" ? (
          <g>
            <rect x="152" y="31" width="24" height="16" rx="3" fill={t.primary} />
            <path d="M164 48v80" stroke={t.primary} strokeDasharray="3 3" />
            <path d="M123 49v154m-5-154h10m-10 154h10" stroke={t.primary} />
            <text x="131" y="172" fontSize="10" fill="currentColor">Sensor height</text>
            <text x="174" y="117" fontSize="10" fill="currentColor">Distance</text>
          </g>
        ) : (
          <rect x="152" y="188" width="24" height="15" rx="3" fill={t.primary} />
        )}
      </svg>
      <Typography component="figcaption" variant="caption" sx={{ color: t.textMuted }}>
        Schematic only. Measure up from the inside floor, in inches.
      </Typography>
    </Box>
  );
}

// deviceTest (optional): { session, deviceId, socket, record, onContinue,
// canContinue }. Passed only when the server advertises the installation
// device-test capability; otherwise no test panel is rendered at all.
export default function CalibrationStep({ value, dispatch, t, darkMode, tankSizing, tankGeometry, deviceTest }) {
  const [distance, setDistance] = useState("");
  const patch = (change) => dispatch({ type: "SET_CALIBRATION", patch: change });
  const check = calibrationCheck(value, distance);
  const offset = numberOrNull(value.sensor_height_offset);
  const full = numberOrNull(value.scale_max_eng);
  const warning = value.sensor_mount_type === "top" &&
    offset !== null && full !== null && offset < full;
  return (
    <Box>
      <Typography variant="h6" sx={{ color: t.textStrong }}>Sensor calibration</Typography>
      <Typography variant="body2" sx={{ color: t.textMuted }} mb={2}>
        All measurements are inches, measured up from the inside tank floor.
      </Typography>
      <TankSizeHelper value={tankSizing} accepted={tankGeometry}
        dispatch={dispatch} t={t} darkMode={darkMode} />
      <ToggleRow
        label="Set calibration with this installation"
        checked={value.enabled}
        onChange={(enabled) => patch({ enabled })}
        description="Turn off to leave any existing calibration unchanged. This does not delete it."
      />
      {value.enabled ? (
        <>
          <TankDiagram mount={value.sensor_mount_type} t={t} />
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">1. Sensor mount</FormLabel>
            <RadioGroup value={value.sensor_mount_type} onChange={(event) => {
              const mount = event.target.value;
              patch({
                sensor_mount_type: mount,
                sensor_height_offset: mount === "bottom" ? "0" : "",
              });
              setDistance("");
            }}>
              <FormControlLabel value="top" control={<Radio />} label="Top of tank - looks down" />
              <FormControlLabel value="bottom" control={<Radio />} label="Bottom-mounted sensor" />
            </RadioGroup>
          </FormControl>
          <Box display="grid" gap={3}>
            <Field label="2. Sensor height (in)" type="number"
              value={value.sensor_height_offset}
              onChange={(event) => patch({ sensor_height_offset: event.target.value })}
              helperText="Tank floor to sensor face. Include a riser; do not measure only to the lid."
              inputProps={{ min: 0, step: "any" }} t={t} darkMode={darkMode} />
            <Field label="3. Empty point (0%)" value="0 - bottom of the tank"
              InputProps={{ readOnly: true }}
              helperText="Fixed at the tank floor for this installation flow."
              t={t} darkMode={darkMode} />
            <Field label="4. Full point (100%) - inches" type="number"
              value={value.scale_max_eng}
              onChange={(event) => patch({ scale_max_eng: event.target.value })}
              helperText="Tank floor to the bottom edge of the inlet pipe, not the tank roof. No inlet? Enter the measured full height."
              inputProps={{ min: 0, step: "any" }} t={t} darkMode={darkMode} />
          </Box>
          {warning ? (
            <StepAlert severity="warning">
              The sensor face is below the full point. Check the mounting height and measurements.
            </StepAlert>
          ) : null}
          {value.sensor_mount_type === "bottom" ? (
            <StepAlert severity="info">
              Confirm this model supports bottom mounting. The quick check is only
              available for top-mounted distance sensors.
            </StepAlert>
          ) : (
            <Box mt={3}>
              <Typography variant="subtitle2" sx={{ color: t.textStrong }} mb={1}>
                Optional quick check
              </Typography>
              <Field label="Current sensor-to-liquid distance (in)" type="number"
                value={distance} onChange={(event) => setDistance(event.target.value)}
                inputProps={{ min: 0, step: "any" }}
                helperText="A manual calculation, not a live reading. This value is not saved."
                t={t} darkMode={darkMode} />
              {distance !== "" && (numberOrNull(distance) === null || Number(distance) < 0) ? (
                <StepAlert severity="warning">Enter a distance of 0 or more inches.</StepAlert>
              ) : null}
              {check ? (
                <StepAlert severity={check.belowFloor ? "warning" : "info"}>
                  Expected height: {check.height.toFixed(1)} in; expected level: {check.percent}%.
                  {check.aboveFull ? " Above the full point, Rivio still shows 100%." : ""}
                  {check.belowFloor ? " Distance exceeds sensor height; check both measurements." : ""}
                </StepAlert>
              ) : null}
            </Box>
          )}
          {calibrationError(value) ? (
            <StepAlert severity="info">{calibrationError(value)}</StepAlert>
          ) : null}
          {deviceTest ? (
            <DeviceTestPanel
              session={deviceTest.session}
              deviceId={deviceTest.deviceId}
              socket={deviceTest.socket}
              record={deviceTest.record}
              onContinue={deviceTest.onContinue}
              canContinue={deviceTest.canContinue}
              calibration={value}
              tankGeometry={tankGeometry}
              dispatch={dispatch}
              t={t}
              darkMode={darkMode}
            />
          ) : null}
        </>
      ) : (
        <StepAlert severity="warning">
          Calibration will not be changed. This does not confirm that an existing
          calibration is present or correct.
        </StepAlert>
      )}
    </Box>
  );
}
